import { useNavigate } from '@tanstack/react-router'
import { useColorScheme } from '@mui/material'
import type maplibregl from 'maplibre-gl'
import React, { useCallback, useContext, useEffect, useRef } from 'react'
import { MapRenderContext } from 'src/contexts/MapRenderContext'
import { useGetMicromobilityMicromobilityGet } from 'src/generated/brezavta/queries'
import { Micromobility } from 'src/generated/brezavta/requests/types.gen'
import { ICON_BASE_SIZE } from 'src/lib/constants'
import { bindCursorPointer, namespaceIconExpression } from 'src/lib/util'

function getIconName(operator: string, icon: string, detail: boolean = false, dark: boolean = false) {
    operator = operator.replace('_ljubljana', 'ljubljana').replace('_maribor', 'maribor')
    operator = operator.split('_')[0]
    const suffix = detail && icon != 'SINGLE' ? (dark ? `${operator}_detail_dark_${icon}` : `${operator}_detail_${icon}`) : `${operator}_${icon}`
    return suffix.toLowerCase()
}

const accepted_providers = [
    // Floating providers should be below stations
    'kvik',
    'sharengo',
    // Station-based systems are next
    'nextbike_cf',
    'nextbike_cn',
    'nextbike_cc',
    'nextbike_ck',
    'nextbike_ce',
    'nextbike_cx',

    'avant2go_si',

    'greengo_544',
    'greengo_603',
    'greengo_1117',
    'greengo_1238',

    'jcdecaux_maribor',
    'jcdecaux_ljubljana',

    'scbikes_gbike',
    'scbikes_pokolo',
    'scbikes_kras',
    'scbikes_sobota',
    'scbikes_gonm',
    'scbikes_posbikes',
    'scbikes_biciklanc',
    'scbikes_pors',
    'scbikes_kulu',

    'micikel',
]

const DETAIL_MIN_ZOOM = 14

function bindMicromobilityInteractions(
    map: React.MutableRefObject<maplibregl.Map | null>,
    layerId: string,
    navigate: ReturnType<typeof useNavigate>,
    cleanups: (() => void)[],
) {
    if (!map.current?.getLayer(layerId)) return

    const unbindCursor = bindCursorPointer(map, layerId)
    cleanups.push(unbindCursor)

    const clickHandler = (e: maplibregl.MapLayerMouseEvent) => {
        if (!e.features?.length) return

        if (e.features.length > 1) {
            const geometry = e.features[0].geometry as unknown as { coordinates: [number, number] }
            map.current!.flyTo({
                center: geometry.coordinates,
                zoom: map.current!.getZoom() + 2,
            })
            return
        }

        const item = e.features[0].properties as { id: string, floating?: number, type?: string }
        if (item.type == 'FLOATING' || item.floating == 1) return
        navigate({ to: `/micromobility/${item.id}` })
    }

    map.current.on('click', layerId, clickHandler)
    cleanups.push(() => {
        if (!map.current) return
        map.current.off('click', layerId, clickHandler)
    })
}

const MicromobilityLayer: React.FC = () => {
    const { map, mapLoaded } = useContext(MapRenderContext)
    const { mode } = useColorScheme()
    const isDark = mode === 'dark'

    const navigate = useNavigate()
    const cleanupsRef = useRef<(() => void)[]>([])

    const { data: micromobilities, refetch } = useGetMicromobilityMicromobilityGet()

    // refetch once every 1 minute
    useEffect(() => {
        const interval = setInterval(() => {
            refetch()
        }, 60000)

        return () => clearInterval(interval)
    }, [refetch])

    const updateAllSources = useCallback(() => {
        if (!map?.current || !micromobilities) return

        const micromobilitySources = Object.keys(map!.current!.getStyle().sources).filter(source => source.startsWith('micromobilities-'))
        // update sources
        for (const source of micromobilitySources) {
            const network = source.split('-')[1]
            const networkStops = micromobilities.filter((m: Micromobility) => m.network === network)
            const sourceInstance = map.current.getSource(source) as maplibregl.GeoJSONSource | undefined
            if (!sourceInstance) continue

            sourceInstance.setData({
                type: 'FeatureCollection',
                features: networkStops.map((m: Micromobility) => ({
                    type: 'Feature',
                    geometry: {
                        type: 'Point',
                        coordinates: [m.lon, m.lat],
                    },
                    properties: {
                        id: m.id,
                        icon: getIconName(m.network || '', m.icon || 'SINGLE', false),
                        detail_icon: getIconName(m.network || '', m.icon || 'SINGLE', true, isDark),
                        title: m.name,
                        capacity: m.spaces,
                        available: m.vehicles || '0',
                        floating: m.type === 'FLOATING' ? 1 : 0,
                        network: m.network,
                    },
                })),
            })
        }
    }, [map, micromobilities, isDark])

    const tryCreateAllSources = useCallback(() => {
        if (!map?.current) return

        for (const network of accepted_providers) {
            const sourceId = `micromobilities-${network.toLowerCase()}`
            const iconLayerId = `micromobility-icon-${network.toLowerCase()}`
            const detailLayerId = `micromobility-detail-${network.toLowerCase()}`

            if (!map.current.getSource(sourceId)) {
                map.current.addSource(sourceId, {
                    type: 'geojson',
                    data: {
                        type: 'FeatureCollection',
                        features: [],
                    },
                })
            }

            if (!map.current.getLayer(iconLayerId)) {
                map.current.addLayer({
                    id: iconLayerId,
                    type: 'symbol',
                    source: sourceId,
                    maxzoom: DETAIL_MIN_ZOOM,
                    layout: {
                        'icon-image': namespaceIconExpression(['get', 'icon']),
                        'icon-size': [
                            'interpolate', ['linear'],
                            ['zoom'],
                            10, ICON_BASE_SIZE * 0.1,
                            13, ICON_BASE_SIZE * 1,
                        ],
                        'icon-allow-overlap': true,
                        'icon-anchor': 'left',
                    },
                })
            }

            if (!map.current.getLayer(detailLayerId)) {
                map.current.addLayer({
                    id: detailLayerId,
                    type: 'symbol',
                    source: sourceId,
                    minzoom: DETAIL_MIN_ZOOM,
                    layout: {
                        'icon-image': namespaceIconExpression(['get', 'detail_icon']),
                        'icon-size': ICON_BASE_SIZE * 1,
                        'text-field': ['format',
                            ['get', 'available'],
                            { 'text-color': ['case', ['==', ['get', 'available'], '0'], isDark ? '#ff6666' : 'red', ['==', ['get', 'available'], 0], isDark ? '#ff6666' : 'red', isDark ? '#66ff66' : 'green'] },
                            '\n',
                            { 'text-color': 'gray' },
                            ['get', 'capacity'],
                            { 'text-color': isDark ? 'white' : 'black' },
                        ],
                        'text-font': ['Inter Bold'],
                        'text-size': [
                            'case',
                            ['==', ['get', 'floating'], 1],
                            0,
                            11,
                        ],
                        'text-offset': [3.5, 0],
                        'icon-allow-overlap': true,
                        'icon-anchor': 'left',
                        'text-allow-overlap': true,
                    },
                })
            }

            bindMicromobilityInteractions(map, iconLayerId, navigate, cleanupsRef.current)
            bindMicromobilityInteractions(map, detailLayerId, navigate, cleanupsRef.current)
        }
    }, [map, isDark, navigate])

    useEffect(() => {
        if (!mapLoaded || !micromobilities) return

        tryCreateAllSources()
        updateAllSources()

        return () => {
            cleanupsRef.current.forEach(fn => fn())
            cleanupsRef.current = []
        }
    }, [mapLoaded, micromobilities, tryCreateAllSources, updateAllSources])

    return null
}

export default MicromobilityLayer
